import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ChevronRight, LogOut, PanelLeft, Search } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import usePermissions from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { isBackendPending } from "@/config/backendPending";
import { NAV_GROUPS, getActiveNavItemId, type NavGroup, type NavItem } from "@/components/layout/navConfig";
import { NAV_BADGE_SOURCES } from "@/components/layout/navBadgeSources";
import athlynLogoWhite from "@/assets/brand/athlyn-logo-white.png";

/** localStorage key holding the rail's collapsed state ("1" / "0"). */
export const NAV_COLLAPSED_STORAGE_KEY = "cms.nav.collapsed";

interface SidebarProps {
  /** `rail` = the desktop menu (collapsible); `drawer` = the phone sheet (always open, no collapse button). */
  variant?: "rail" | "drawer";
  /** Opens the Ctrl/⌘ K command palette (mounted once in `Shell`). */
  onOpenPalette?: () => void;
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    // Storage blocked — the state simply is not remembered.
  }
}

/** Whether the browser runs on a Mac (for the ⌘K hint). */
export function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform ?? nav.platform ?? "";
  return /mac/i.test(platform);
}

function initialsOf(firstName?: string, lastName?: string): string {
  const initials = `${firstName?.trim()[0] ?? ""}${lastName?.trim()[0] ?? ""}`.toUpperCase();
  return initials || "?";
}

interface NavBadgeProps {
  count: number;
  collapsed: boolean;
}

/** Pink count pill (a dot when collapsed); purely visual — the count is in the link's label. */
function NavBadge({ count, collapsed }: NavBadgeProps) {
  if (collapsed) {
    return (
      <span
        data-nav-badge-dot=""
        aria-hidden="true"
        className="absolute right-3 top-1.5 h-2 w-2 rounded-full bg-ds-pink ring-2 ring-ds-nav"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="ml-auto h-[18px] min-w-[20px] rounded-full bg-ds-pink px-1.5 text-center font-ds-mono text-[11px] font-semibold leading-[18px] text-white"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

interface NavLinkProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  nested?: boolean;
  /** Badge count from the registry; `> 0` renders the pill and extends the accessible name. */
  count?: number;
}

function NavLink({ item, active, collapsed, nested = false, count }: NavLinkProps) {
  const Icon = item.icon;
  const showBadge = typeof count === "number" && count > 0;
  const link = (
    <Link
      href={item.path}
      aria-current={active ? "page" : undefined}
      aria-label={showBadge ? `${item.label}, ${count} need attention` : undefined}
      className={cn(
        "relative flex w-full items-center gap-2.5 rounded-lg px-2.5 font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent",
        nested ? "h-[30px] text-[13px]" : "h-[34px] text-[13.5px]",
        active
          ? "bg-ds-nav2 text-white before:absolute before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-[3px] before:bg-ds-accent"
          : "text-ds-navink hover:bg-white/5 hover:text-white",
        active && (nested ? "before:-left-[34px]" : "before:-left-2"),
        collapsed && "justify-center px-0"
      )}
    >
      <Icon className="h-4 w-4 flex-none" aria-hidden="true" />
      <span className={cn("truncate", collapsed && "sr-only")}>{item.label}</span>
      {showBadge && <NavBadge count={count} collapsed={collapsed} />}
    </Link>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

interface BadgedNavLinkProps extends NavLinkProps {
  useCount: () => number | undefined;
}

/** One component per badged item, so its registry hook is called unconditionally (rules of hooks). */
function BadgedNavLink({ useCount, ...props }: BadgedNavLinkProps) {
  const count = useCount();
  return <NavLink {...props} count={count} />;
}

/** The item's link, with its registry badge when `NAV_BADGE_SOURCES` has an entry for it. */
function NavEntry(props: NavLinkProps) {
  const useCount = NAV_BADGE_SOURCES[props.item.id];
  return useCount ? <BadgedNavLink {...props} useCount={useCount} /> : <NavLink {...props} />;
}

interface NavGroupSectionProps {
  group: NavGroup;
  items: NavItem[];
  activeId: string | null;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}

function NavGroupSection({ group, items, activeId, collapsed, open, onToggle }: NavGroupSectionProps) {
  const header = group.label && !collapsed && (
    <h2 className="px-2.5 pb-1 pt-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ds-navhd">{group.label}</h2>
  );
  const links = (nested: boolean) =>
    items.map((item) => (
      <li key={item.id}>
        <NavEntry item={item} active={item.id === activeId} collapsed={collapsed} nested={nested} />
      </li>
    ));

  // Collapsed rail: every child stays reachable as an icon link (no hidden sub-list).
  if (!group.collapsible || collapsed) {
    return (
      <div data-nav-group={group.id} className={cn(collapsed && group.label && "mt-1.5 border-t border-white/5 pt-1.5")}>
        {header}
        <ul className="flex flex-col">{links(false)}</ul>
      </div>
    );
  }

  const GroupIcon = group.collapsible.icon;
  const listId = `nav-group-${group.collapsible.id}`;
  return (
    <div data-nav-group={group.id}>
      {header}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={onToggle}
        className="flex h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13.5px] font-medium text-ds-navink transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent"
      >
        <GroupIcon className="h-4 w-4 flex-none" aria-hidden="true" />
        <span className="truncate">{group.collapsible.label}</span>
        <ChevronRight
          className={cn("ml-auto h-3.5 w-3.5 opacity-60 transition-transform motion-reduce:transition-none", open && "rotate-90")}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul id={listId} className="flex flex-col pl-[26px]">
          {links(true)}
        </ul>
      )}
    </div>
  );
}

/**
 * The CMS menu (C-CMS-DS): dark navy grouped rail in both themes, Geist, prefix-match
 * active item, registry-driven badges, user footer with Sign out, collapse remembered
 * in localStorage (`cms.nav.collapsed`).
 */
export default function Sidebar({ variant = "rail", onOpenPalette }: SidebarProps) {
  const isDrawer = variant === "drawer";
  const [location] = useLocation();
  const { canAccessMenu, isAthlynAdmin } = usePermissions();
  const { user, logout, isLoggingOut } = useAuth();
  const [storedCollapsed, setStoredCollapsed] = useState<boolean>(readCollapsed);
  const collapsed = !isDrawer && storedCollapsed;

  // Same two-axis rule as before: roles (`canAccessMenu`) AND backend existence.
  const visibleGroups = NAV_GROUPS.map((group) => ({
    group,
    items: group.items.filter((item) => canAccessMenu(item.id) && !isBackendPending(item.id)),
  })).filter((entry) => entry.items.length > 0);

  const activeId = getActiveNavItemId(location, visibleGroups.flatMap((entry) => entry.items));
  const groupOfActive = visibleGroups.find((entry) => entry.items.some((item) => item.id === activeId))?.group;

  // Collapsible groups default closed; the group holding the active item opens itself.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    groupOfActive?.collapsible ? { [groupOfActive.id]: true } : {}
  );
  useEffect(() => {
    if (groupOfActive?.collapsible) {
      setOpenGroups((prev) => (prev[groupOfActive.id] ? prev : { ...prev, [groupOfActive.id]: true }));
    }
  }, [groupOfActive]);

  const toggleCollapsed = () => {
    const next = !storedCollapsed;
    setStoredCollapsed(next);
    writeCollapsed(next);
  };

  const handleSignOut = async () => {
    try {
      await logout.mutateAsync();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "";
  const roleLine = isAthlynAdmin ? "Athlyn admin" : "Athlyn team";
  const shortcut = isMacPlatform() ? "⌘K" : "Ctrl K";
  const iconButton =
    "grid h-[30px] w-[30px] flex-none place-items-center rounded-lg text-ds-navink transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent disabled:opacity-50";

  return (
    <TooltipProvider delayDuration={200}>
      <nav
        aria-label="Main navigation"
        data-collapsed={collapsed ? "true" : "false"}
        className={cn(
          "flex h-full flex-col bg-ds-nav font-ds text-ds-navink transition-[width] duration-150 motion-reduce:transition-none",
          isDrawer ? "w-full" : collapsed ? "w-16" : "w-[248px]"
        )}
      >
        {/* Brand */}
        <div className={cn("flex h-14 flex-none items-center gap-2 border-b border-white/5 px-4", collapsed && "justify-center px-0")}>
          <img
            src={athlynLogoWhite}
            alt="Athlyn"
            className={cn("block", collapsed ? "h-7 w-7 object-cover object-left" : "h-[18px] w-auto")}
          />
          {!collapsed && (
            <span className="rounded-[5px] bg-ds-accent/20 px-1.5 font-ds-mono text-[10px] font-semibold leading-[18px] tracking-[0.08em] text-ds-navink">
              CMS
            </span>
          )}
        </div>

        {/* Search or jump to… */}
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Search or jump to"
                onClick={onOpenPalette}
                className={cn(iconButton, "mx-auto mb-1.5 mt-3 h-[34px] w-[34px] bg-white/5")}
              >
                <Search className="h-4 w-4" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Search or jump to… ({shortcut})</TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={onOpenPalette}
            className="mx-3 mb-1.5 mt-3 flex h-[34px] flex-none items-center gap-2 rounded-lg bg-white/5 px-2.5 text-left text-[13px] text-ds-navink/80 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent"
          >
            <Search className="h-[15px] w-[15px] flex-none" aria-hidden="true" />
            <span>Search or jump to…</span>
            <kbd className="ml-auto rounded-[5px] border border-white/10 px-1.5 font-ds-mono text-[11px] font-medium">
              {shortcut}
            </kbd>
          </button>
        )}

        {/* Menu */}
        <div className="flex-1 overflow-y-auto px-2 pb-2 pt-1 [scrollbar-width:none]">
          {visibleGroups.map(({ group, items }) => (
            <NavGroupSection
              key={group.id}
              group={group}
              items={items}
              activeId={activeId}
              collapsed={collapsed}
              open={Boolean(openGroups[group.id])}
              onToggle={() => setOpenGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
            />
          ))}
        </div>

        {/* Footer: who is signed in, collapse, sign out */}
        <div
          className={cn(
            "flex flex-none items-center gap-2.5 border-t border-white/5 px-3 py-2.5",
            collapsed && "flex-col gap-1.5 px-0"
          )}
        >
          <span
            aria-hidden="true"
            title={collapsed ? fullName : undefined}
            className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-ds-brand text-xs font-bold text-white"
          >
            {initialsOf(user?.firstName, user?.lastName)}
          </span>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <b className="block truncate text-[12.5px] font-semibold text-white" title={fullName}>
                {fullName}
              </b>
              <span className="block text-[11.5px] text-ds-navink/80">{roleLine}</span>
            </div>
          )}
          {!isDrawer && (
            <button
              type="button"
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              title={collapsed ? "Expand menu" : "Collapse menu"}
              onClick={toggleCollapsed}
              className={iconButton}
            >
              <PanelLeft className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            aria-label="Sign out"
            title="Sign out"
            onClick={handleSignOut}
            disabled={isLoggingOut}
            className={iconButton}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </nav>
    </TooltipProvider>
  );
}
